import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import { uploadDepreciationexcel } from '../../../services/asset.service';
import * as CryptoJS from 'crypto-js';
import './UploadDepreciation.css';

const templatePath     = '/exceltemplates/Upload_Depreciation_AS.xlsx';
const download_img_Url = '/new-download.gif';

/* ── SVG Download Icon ── */
const DownloadSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
    <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
    <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
  </svg>
);

/* ── Loading Overlay ── */
const LoadingOverlay = () => (
  <div className="ud-loading-overlay">
    <div className="ud-loading-card">
      <div className="ud-loading-spinner">
        <img src={download_img_Url} alt="Uploading" />
      </div>
      <h5 className="ud-loading-title">Uploading Depreciation Data…</h5>
      <div className="ud-loading-progress">
        <div className="ud-loading-bar" />
      </div>
      <p className="ud-loading-subtitle">
        <i className="bi bi-info-circle-fill" />
        Please don&apos;t refresh the page until the process is completed
      </p>
    </div>
  </div>
);

/* ============================================================
   UploadDepreciation Component
   ============================================================ */
function UploadDepreciation() {

  const errRef = useRef();
  const [errMsg,  setErrMsg]  = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items,   setItems]   = useState([]);
  const [fileName, setFileName] = useState('');

  /* original cell edit handler */
  var onCellEditStopFn = (event, newCell) => {
    var obj = {};
    obj[newCell.field] = event.target.value;
  };

  /* original useEffect (kept as-is) */
  useEffect(() => {
    /* getAssets().then(res=>{
      if(res?.data?.data?.length) setItems(res.data.data);
    }) */
  }, []);

  /* original upload handler */
  const onChangeFile = async (event) => {
    try {
      setLoading('..');
      setFileName(event.target.files[0]?.name || '');
      setErrMsg('');
      setSuccess(false);

      const data = new FormData();
      data.append('file',        event.target.files[0]);
      data.append('company_id',  sessionStorage.getItem('LoggedInCompanyId'));
      data.append('division_id', sessionStorage.getItem('LoggedInDivisionId'));
      data.append('created_by',  sessionStorage.getItem('LoggedInUserId'));

      uploadDepreciationexcel(data).then(res => {
        if (typeof res.data.errors !== 'undefined' && res.data.errors.errno > 0) {
          setErrMsg(res.data.message);
          setLoading(false);
          setSuccess(false);
        } else {
          setSuccess(res.data.message);
          setLoading(false);
        }
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
      setErrMsg(error.data.message);
    }
  };

  /* ====================================================
     RENDER
     ==================================================== */
  return (
    <div className="ud-container">

      {/* ── Loading Overlay ── */}
      {loading && <LoadingOverlay />}

      {/* ════════════════════════
          PAGE HEADER
          ════════════════════════ */}
      <div className="ud-page-header">
        <div className="ud-header-content">
          <div className="ud-header-left">
            <div className="ud-header-icon">
              <i className="bi bi-graph-down-arrow" style={{ fontSize: '1.5rem' }} />
            </div>
            <div>
              <h1 className="ud-page-title">Upload Depreciation (AS)</h1>
              <p className="ud-page-subtitle">Upload depreciation data using the Excel template</p>
            </div>
          </div>
          <a href="/depreciation" className="ud-list-btn">
            <i className="bi bi-list-ul" /> Depreciation Data Log
          </a>
        </div>

        {/* Alert Banners */}
        {success ? (
          <div className="ud-alert ud-alert-success" role="alert">
            <i className="bi bi-check-circle-fill" /> {success}
          </div>
        ) : errMsg ? (
          <div className="ud-alert ud-alert-danger" role="alert" ref={errRef}>
            <i className="bi bi-exclamation-triangle-fill" /> {errMsg}
          </div>
        ) : null}
      </div>

      {/* ════════════════════════
          UPLOAD CARD
          ════════════════════════ */}
      <div className="ud-card-grid">
        <div className="ud-upload-card" id="depreciation-upload-card">

          {/* Card Header */}
          <div className="ud-card-header">
            <div className="ud-card-icon">
              <i className="bi bi-file-earmark-bar-graph" style={{ fontSize: '1.4rem' }} />
            </div>
            <div className="ud-card-header-text">
              <h4>Depreciation Excel Upload</h4>
              <p>Upload depreciation schedule via Excel (.xlsx)</p>
            </div>
          </div>

          {/* Card Body */}
          <div className="ud-card-body">

            {/* Upload Drop Zone */}
            <div className="ud-upload-zone" id="depreciation-upload-zone">
              <div className="ud-upload-icon">
                <i className="bi bi-cloud-upload" />
              </div>
              <p className="ud-upload-zone-title">
                Select your Excel file here
              </p>
              <p className="ud-upload-zone-hint">
                Only <strong>.xlsx</strong> format is accepted &nbsp;&mdash;&nbsp;
                <span className="ud-required">Required *</span>
              </p>
              <Button
                variant="contained"
                component="label"
                className="ud-upload-btn"
                id="depreciation-upload-btn"
              >
                <i className="bi bi-upload" style={{ marginRight: 8 }} />
                Choose File
                <input hidden type="file" accept=".xlsx" onChange={onChangeFile} />
              </Button>

              {/* File name preview */}
              {fileName && !loading && (
                <div className="ud-file-badge">
                  <i className="bi bi-file-earmark-spreadsheet-fill" /> {fileName}
                </div>
              )}

              {/* Inline processing indicator */}
              {loading && (
                <div className="ud-processing">
                  <span className="ud-dot" /><span className="ud-dot" /><span className="ud-dot" />
                  Processing file…
                </div>
              )}
            </div>

            {/* Download Template */}
            <div className="ud-template-row">
              <div className="ud-template-info">
                <i className="bi bi-info-circle" />
                Use the official template to ensure correct data format
              </div>
              <a href={templatePath} className="ud-download-btn" id="depreciation-template-link">
                <DownloadSVG /> Download Template
              </a>
            </div>
          </div>
        </div>

        {/* ── Instructions Panel ── */}
        <div className="ud-instructions-card" id="depreciation-instructions-card">
          <div className="ud-instructions-header">
            <span className="ud-instructions-badge">i</span>
            <h5>Upload Instructions</h5>
          </div>
          <ul className="ud-instructions-list">
            {[
              { icon: 'bi-file-earmark-check',     text: <>Only <strong>.xlsx</strong> Excel files are accepted.</> },
              { icon: 'bi-calendar-date',           text: <>Use <code>YYYY-MM-DD</code> format for all date fields.</> },
              { icon: 'bi-dash-circle',             text: <>Use <strong>&quot;NA&quot;</strong> for blank text cells and <strong>&quot;0&quot;</strong> for blank numeric cells.</> },
              { icon: 'bi-building',                text: <>Company: <code>{sessionStorage.getItem('LoggedInCompanyName')}</code></> },
              { icon: 'bi-geo-alt',                 text: <>Branch: <code>{sessionStorage.getItem('LoggedInDivisionName')}</code></> },
              { icon: 'bi-arrow-repeat',            text: <>Do <strong>not</strong> refresh the page during upload.</> },
            ].map((item, idx) => (
              <li key={idx} id={`dep-instruction-${idx + 1}`}>
                <span className="ud-instr-num">{idx + 1}</span>
                <i className={`bi ${item.icon} ud-instr-icon`} />
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

    </div>
  );
}

export default UploadDepreciation;